import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { not, trim } from "ramda";
import React from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import { CREATE_TOPIC } from "../gql/mutations/topic";
import { QUERY_TOPICS } from "../gql/queries/topic";

import { Spacing } from "../types/enum";

/**
 * Types
 */
interface Props {
  open: boolean;
  onClose: () => void;
  limit: number;
  offset: number;
}

interface FormValues {
  title: string;
}

/**
 * Schema
 */
const schema = yup.object().shape({
  title: yup.string().required(),
});

const TopicsPageCreateTopicCont: React.FC<Props> = ({ open, onClose, limit, offset }: Props) => {
  const [createTopic, { loading }] = useMutation(CREATE_TOPIC, {
    refetchQueries: [{ query: QUERY_TOPICS, variables: { filters: { limit, offset } } }],
  });

  const { register, formState, handleSubmit } = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: SubmitHandler<FormValues> = async (form) => {
    const variables = {
      input: {
        title: trim(form.title),
      },
    };

    try {
      await createTopic({ variables });
      enqueueSnackbar("Topic successfully created", { variant: "success" });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <Dialog maxWidth="lg" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box mb={Spacing.sm}>
            <Typography variant="body1" color="textPrimary">
              Topic
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please input the title of the topic that you would like to create.
            </Typography>
          </Box>

          <FormControl fullWidth>
            <TextField label="Title" variant="outlined" {...register("title")} />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={not(formState.isValid) || loading}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TopicsPageCreateTopicCont;
